import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Search, Plus, ChevronRight } from 'lucide-react';
import {
  RESOURCE_CATALOG,
  searchCatalogTemplates,
  type ResourceTemplate,
  type CatalogProvider,
  type CatalogGroup,
  type CatalogSearchResult,
} from '../resourceCatalog';

// ==========================================
// Types
// ==========================================

export interface ResourceCatalogMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: ResourceTemplate) => void;
  onNewBlank: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

// ==========================================
// Styles
// ==========================================

const PANEL_STYLE: React.CSSProperties = {
  position: 'absolute',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 10001,
  minWidth: 200,
  maxHeight: 360,
  overflowY: 'auto',
  padding: '4px 0',
};

const ITEM_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  fontSize: 11,
  cursor: 'pointer',
  color: '#374151',
  whiteSpace: 'nowrap',
};

const ITEM_HOVER_BG = '#f1f5f9';
const ACTIVE_HOVER_BG = '#e0e7ff';

// ==========================================
// CatalogPanel — renders a single flyout panel
// ==========================================

type PanelItem = {
  type: 'group';
  group: CatalogGroup;
} | {
  type: 'provider';
  provider: CatalogProvider;
} | {
  type: 'template';
  template: ResourceTemplate;
};

interface CatalogPanelProps {
  items: PanelItem[];
  position: { top: number; left: number };
  activeId: string | null;
  onHover: (id: string | null) => void;
  onClickTemplate: (template: ResourceTemplate) => void;
}

const CatalogPanel = memo(function CatalogPanel({ items, position, activeId, onHover, onClickTemplate }: CatalogPanelProps) {
  return (
    <div style={{ ...PANEL_STYLE, top: position.top, left: position.left }}>
      {items.map((item) => {
        if (item.type === 'template') {
          const t = item.template;
          const costField = t.fields.find(f => f.showOnBadge);
          return (
            <div
              key={t.id}
              style={{ ...ITEM_STYLE }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ITEM_HOVER_BG; onHover(null); }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              onClick={() => onClickTemplate(t)}
            >
              <span style={{ fontWeight: 500, minWidth: 60 }}>{t.name}</span>
              <span style={{ color: '#94a3b8', fontSize: 10, flex: 1 }}>{t.description}</span>
              {costField && (
                <span style={{ fontSize: 10, fontWeight: 500, color: t.color, marginLeft: 4 }}>
                  {'\u20ac'}{costField.value}
                </span>
              )}
            </div>
          );
        }

        const id = item.type === 'provider' ? item.provider.id : item.group.id;
        const label = item.type === 'provider' ? item.provider.name : item.group.label;
        const color = item.type === 'provider' ? item.provider.color : undefined;
        const isActive = activeId === id;

        return (
          <div
            key={id}
            style={{
              ...ITEM_STYLE,
              backgroundColor: isActive ? ACTIVE_HOVER_BG : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = ITEM_HOVER_BG;
              onHover(id);
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {color && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
            )}
            <span style={{ flex: 1 }}>{label}</span>
            <ChevronRight size={10} style={{ color: '#94a3b8' }} />
          </div>
        );
      })}
    </div>
  );
});

// ==========================================
// SearchResultsPanel
// ==========================================

interface SearchResultsPanelProps {
  results: CatalogSearchResult[];
  position: { top: number; left: number };
  onClickTemplate: (template: ResourceTemplate) => void;
  noResultsLabel: string;
}

function SearchResultsPanel({ results, position, onClickTemplate, noResultsLabel }: SearchResultsPanelProps) {
  return (
    <div style={{ ...PANEL_STYLE, top: position.top, left: position.left, minWidth: 280 }}>
      {results.length === 0 ? (
        <div style={{ padding: '12px 10px', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
          {noResultsLabel}
        </div>
      ) : (
        results.map((r) => {
          const costField = r.template.fields.find(f => f.showOnBadge);
          return (
            <div
              key={r.template.id}
              style={{ ...ITEM_STYLE }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ITEM_HOVER_BG; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              onClick={() => onClickTemplate(r.template)}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: r.template.color, flexShrink: 0 }} />
              <span style={{ fontWeight: 500 }}>{r.template.name}</span>
              <span style={{ color: '#94a3b8', fontSize: 10, flex: 1 }}>{r.template.description}</span>
              {costField && (
                <span style={{ fontSize: 10, fontWeight: 500, color: r.template.color, marginLeft: 4 }}>
                  {'\u20ac'}{costField.value}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ==========================================
// ResourceCatalogMenu (Root)
// ==========================================

export const ResourceCatalogMenu = memo(function ResourceCatalogMenu({
  isOpen,
  onClose,
  onSelectTemplate,
  onNewBlank,
  anchorRef,
}: ResourceCatalogMenuProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const [openPath, setOpenPath] = useState<string[]>([]);

  // Reset state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      setOpenPath([]);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, onClose]);

  // Compute anchor position
  const anchorRect = useMemo(() => {
    if (!isOpen || !anchorRef.current) return null;
    return anchorRef.current.getBoundingClientRect();
  }, [isOpen, anchorRef]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchText.trim()) return null;
    return searchCatalogTemplates(searchText);
  }, [searchText]);

  const handleClickTemplate = useCallback((template: ResourceTemplate) => {
    onSelectTemplate(template);
  }, [onSelectTemplate]);

  if (!isOpen || !anchorRect) return null;

  // Root panel position: below the anchor button, aligned left
  const rootPos = { top: anchorRect.bottom + 4, left: anchorRect.left };

  // Build nested panels based on openPath
  const panels: React.ReactNode[] = [];

  if (searchResults !== null) {
    // Search mode: show flat results
    panels.push(
      <SearchResultsPanel
        key="search"
        results={searchResults}
        position={rootPos}
        onClickTemplate={handleClickTemplate}
        noResultsLabel={t('resource.catalog.noResults')}
      />
    );
  } else {
    // Level 0: Provider list + "New blank" option
    const rootItems: PanelItem[] = RESOURCE_CATALOG.map(p => ({ type: 'provider' as const, provider: p }));

    panels.push(
      <div key="root" style={{ ...PANEL_STYLE, top: rootPos.top, left: rootPos.left }}>
        {/* Search input */}
        <div style={{ padding: '4px 8px 4px 8px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ position: 'relative' }}>
            <Search size={10} style={{ position: 'absolute', left: 6, top: 6, color: '#94a3b8' }} />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('resource.catalog.search')}
              autoFocus
              style={{
                width: '100%', fontSize: 10, padding: '3px 6px 3px 20px',
                border: '1px solid #e2e8f0', borderRadius: 4, outline: 'none',
                backgroundColor: '#fff', color: '#1f2937',
              }}
            />
          </div>
        </div>

        {/* Provider items */}
        {rootItems.map((item) => {
          if (item.type !== 'provider') return null;
          const p = item.provider;
          const isActive = openPath[0] === p.id;
          return (
            <div
              key={p.id}
              style={{
                ...ITEM_STYLE,
                backgroundColor: isActive ? ACTIVE_HOVER_BG : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = ITEM_HOVER_BG;
                setOpenPath([p.id]);
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: p.color, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{p.name}</span>
              <ChevronRight size={10} style={{ color: '#94a3b8' }} />
            </div>
          );
        })}

        {/* Divider + New blank */}
        <div style={{ height: 1, backgroundColor: '#f1f5f9', margin: '2px 0' }} />
        <div
          style={{ ...ITEM_STYLE }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ITEM_HOVER_BG; setOpenPath([]); }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          onClick={onNewBlank}
        >
          <Plus size={10} style={{ color: '#3b82f6' }} />
          <span>{t('resource.catalog.newBlank')}</span>
        </div>
      </div>
    );

    // Walk the openPath and render sub-panels
    if (openPath.length > 0) {
      const provider = RESOURCE_CATALOG.find(p => p.id === openPath[0]);
      if (provider) {
        // Level 1: main categories
        const level1Pos = { top: rootPos.top, left: rootPos.left + 208 };
        const level1Items: PanelItem[] = provider.groups.map(g => {
          if (g.templates && !g.children) {
            // Has direct templates — show as group
            return { type: 'group' as const, group: g };
          }
          return { type: 'group' as const, group: g };
        });

        panels.push(
          <CatalogPanel
            key="level1"
            items={level1Items}
            position={level1Pos}
            activeId={openPath[1] ?? null}
            onHover={(id) => setOpenPath(id ? [openPath[0], id] : [openPath[0]])}
            onClickTemplate={handleClickTemplate}
          />
        );

        // Level 2: sub-categories or templates
        if (openPath.length >= 2) {
          const mainGroup = provider.groups.find(g => g.id === openPath[1]);
          if (mainGroup) {
            const level2Pos = { top: rootPos.top, left: rootPos.left + 208 + 208 };

            if (mainGroup.children) {
              // Has sub-groups
              const level2Items: PanelItem[] = mainGroup.children.map(g => ({ type: 'group' as const, group: g }));
              panels.push(
                <CatalogPanel
                  key="level2"
                  items={level2Items}
                  position={level2Pos}
                  activeId={openPath[2] ?? null}
                  onHover={(id) => setOpenPath(id ? [openPath[0], openPath[1], id] : [openPath[0], openPath[1]])}
                  onClickTemplate={handleClickTemplate}
                />
              );

              // Level 3: templates
              if (openPath.length >= 3) {
                const subGroup = mainGroup.children.find(g => g.id === openPath[2]);
                if (subGroup?.templates) {
                  const level3Pos = { top: rootPos.top, left: rootPos.left + 208 + 208 + 208 };
                  const level3Items: PanelItem[] = subGroup.templates.map(t => ({ type: 'template' as const, template: t }));
                  panels.push(
                    <CatalogPanel
                      key="level3"
                      items={level3Items}
                      position={level3Pos}
                      activeId={null}
                      onHover={() => {}}
                      onClickTemplate={handleClickTemplate}
                    />
                  );
                }
              }
            } else if (mainGroup.templates) {
              // Direct templates (e.g. Managed Server)
              const level2Items: PanelItem[] = mainGroup.templates.map(t => ({ type: 'template' as const, template: t }));
              panels.push(
                <CatalogPanel
                  key="level2"
                  items={level2Items}
                  position={level2Pos}
                  activeId={null}
                  onHover={() => {}}
                  onClickTemplate={handleClickTemplate}
                />
              );
            }
          }
        }
      }
    }
  }

  return createPortal(
    <div ref={rootRef}>
      {panels}
    </div>,
    document.body,
  );
});
